/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AdcModel, DashboardDataModel, WidgetModel } from '../../../../models';
import { Component } from '@angular/core';
import { DashboardService } from '../../../../services/component';
import { UtilsService } from '../../../../../../../core/services';
import { WidgetComponent } from '../widgetBaseComponent/widget.component';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-security-widget',
    templateUrl: '../widgetBaseComponent/widget.component.html',
    styleUrls: ['../widgetBaseComponent/widget.component.scss']
})
export class SecurityWidgetComponent extends WidgetComponent {
    /**
     * Data has loaded
     */
    public loaded: boolean = false;

    /**
     * Store the formatted data for the view.
     */
    public view: WidgetModel;

    constructor(
        dashboardService: DashboardService,
        utilsService: UtilsService
    ) {
        super(dashboardService, utilsService);

        const data: DashboardDataModel = this.dashboardService.allData;
        this.setUpPage(data);

        this.dashboardService.allDataEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                data => {
                    this.setUpPage(data);
                }
            );
    }

    /**
     * Set up page.
     * @param data
     * @private
     */
    private setUpPage(data: any): void {
        const securityDetails: any = this.formatSecurityData(data.insights);
        const adcsWithRiskCount: number = data.adcs?.filter((item: AdcModel) => item.vulnerable)?.length;
        this.view = this.getViewData(securityDetails, adcsWithRiskCount);
        this.loaded = true;
    }

    /**
     * Format ADC data.
     * @private
     * @param insights
     */
    private formatSecurityData(insights: any[] = []): any {
        let critical: number = 0, high: number = 0, medium: number = 0, low: number = 0;
        const securityRisks: any[] = insights.map(item => item.securityRisk);
        for (const risk of securityRisks) {
            critical = critical + risk.critical;
            high = high + risk.high;
            medium = medium + risk.medium;
            low = low + risk.low;
        }

        return { critical, high, medium, low };
    }

    /**
     * Get security data.
     * @param securityDetails
     * @private
     */
    private getViewData(securityDetails: {
        critical: number;
        high: number;
        medium: number;
        low: number;
    }, adcsWithRiskCount: number): WidgetModel {
        let viewObject: WidgetModel = this.getBaseMessaging(this.baseView);

        const error: any = this.errors?.insights;
        if (error) {
            return this.getErrorMessaging(viewObject, error);
        }

        if (securityDetails.critical || securityDetails.high || securityDetails.medium || securityDetails.low) {
            viewObject = this.getActiveMessaging(viewObject, securityDetails, adcsWithRiskCount);
        }

        return viewObject;
    }

    /**
     * Get base view messaging.
     * @param baseView
     * @private
     */
    private getBaseMessaging(baseView: WidgetModel): WidgetModel {
        const baseData = {
            iconSrc: 'assets/centralisedManagement/icons/security.svg',
            title: 'security',
            flagState: 'success',
            counters: [{ count: 0, countSubTitle: 'Risks', subTitleClass: 'success' }],
            message: 'You do not have any security risks at the moment.',
            linkText: 'View my Security insights',
            linkUrl: '/cm/security/insights',
        };

        return this.updateObject(baseView, baseData);
    }

    /**
     * Get active messaging.
     * @param viewObject
     * @param securityDetails
     * @private
     */
    private getActiveMessaging(viewObject: WidgetModel, securityDetails: {
        critical: number;
        high: number;
        medium: number;
        low: number;
    }, adcsWithRiskCount: number): WidgetModel {
        const risksTotal: number = securityDetails.critical + securityDetails.high + securityDetails.medium + securityDetails.low;
        const newData = {
            flagState: this.getFlagState(securityDetails),
            subtitle: null,
            // eslint-disable-next-line max-len
            message: `You have a total of <strong>${risksTotal}</strong> security warnings, across <strong>${adcsWithRiskCount}</strong> ADCs.`,
            linkText: 'View my Security insights',
            linkUrl: '/cm/security/insights',
            counters: [
                {
                    count: securityDetails.critical,
                    countSubTitle: 'Critical',
                    subTitleClass: 'tertiary'
                },
                {
                    count: securityDetails.high,
                    countSubTitle: 'High',
                    subTitleClass: 'secondary'
                },
                {
                    count: securityDetails.medium,
                    countSubTitle: 'Medium',
                    subTitleClass: 'quinary'
                },
                {
                    count: securityDetails.low,
                    countSubTitle: 'Low',
                    subTitleClass: 'success'
                }
            ]
        };

        return this.updateObject(viewObject, newData);
    }

    /**
     * Get flag state.
     * @param securityDetails
     * @private
     */
    private getFlagState(securityDetails: { critical: any; high: any; medium: any; low: any; }): string {
        if (securityDetails.critical || securityDetails.high) {
            return 'error';
        }

        if (securityDetails.medium || securityDetails.low) {
            return 'warn';
        }
    }
}
